import { AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  DialogContentService,
  INSTITUTION_CONTEXT_TOKEN,
  IPersonsCollaborations,
  ISTPersonActivity,
} from 'aa-ng';
import {
  AaNgD3Module,
  CollaborativeNetworkAndActivitiesListViewType,
  PeopleCollaborativeActivitiesComponent,
} from 'aa-ng-d3';
import { BehaviorSubject, catchError, EMPTY, switchMap, tap } from 'rxjs';
import {
  EdsCollaborationGraphDefaults,
  ExtendedForceDirectedGraphNode,
  ICollaborationGraph,
  IInstitutionContext,
  mapCollaborationGraph,
  toCollaborationActivityRefs,
  toCollaboratorPerson,
} from 'src/app/models';

import { IPersonSearchResult } from '../../models/person-search';
import {
  ActivityCollaboratorsService,
  CollaborationGraphService,
  InstitutionsService,
  MAX_SCHOLARS_TO_DISPLAY,
} from '../../services';
import { BaseDirective } from '../base.component';

@Component({
  selector: 'app-relevancy-collaborations',
  imports: [AaNgD3Module, AsyncPipe],
  templateUrl: './relevancy-collaborations.component.html',
})
export class RelevancyCollaborationsComponent
  extends BaseDirective
  implements OnInit
{
  @Inject(INSTITUTION_CONTEXT_TOKEN) institutionsService =
    inject<InstitutionsService>(INSTITUTION_CONTEXT_TOKEN);
  private activityCollaboratorsService = inject(ActivityCollaboratorsService);
  private collaborationGraphService = inject(CollaborationGraphService);
  private dialogContentService = inject(DialogContentService);
  private route = inject(ActivatedRoute);

  activities: ISTPersonActivity[];
  collaborations: IPersonsCollaborations[];

  /** The graph as the server sent it - its edges carry the ids the dialog drills into. */
  collaborationGraph: ICollaborationGraph;
  institutionContext: IInstitutionContext;
  collaborativeNetworkAndActivitiesListCurrentView =
    CollaborativeNetworkAndActivitiesListViewType.Network;

  @Input() person: IPersonSearchResult;

  /**
   * The only graph control on this panel.
   *
   * There is deliberately no top-scholars dropdown: this graph names its seed - the scholar being
   * viewed - and the server ignores a seed count when the seeds are given, so the control could only
   * ever look like it did something. AMS hides it on its equivalent panel for the same reason.
   */
  collaboratorCount$ = new BehaviorSubject<number>(
    EdsCollaborationGraphDefaults.collaboratorsPerScholar,
  );

  /** Shared with the fetch, which asks for one more than this to know if there is a next page. */
  maxScholarsToDisplay = MAX_SCHOLARS_TO_DISPLAY;

  /**
   * Toggled after `showPeople` mutates a row's `contributors` in place, since that mutation keeps
   * the row's object identity and `aa-activity`'s change detection is keyed on that reference.
   * No-ops until `aa-ng-d3` publishes the forwarding input - see the doc comment on it.
   */
  forceMapPeople = false;

  @Output() loading = new EventEmitter<boolean>();

  constructor() {
    super();
    this._subscriptions.push(
      this.institutionsService.context.subscribe((context) => {
        this.institutionContext = context;
      }),
    );
  }

  ngOnInit(): void {
    // `switchMap` abandons an in-flight graph when the slider moves again.
    this._subscriptions.push(
      this.collaboratorCount$
        .pipe(
          switchMap((collaboratorsPerScholar) => {
            this.loading.emit(true);
            return this.collaborationGraphService
              .getByTopic({
                query: this.route.snapshot.queryParams['query'],
                seedPersonGuid: this.person.guid,
                collaboratorsPerScholar,
              })
              .pipe(
                tap((graph) => {
                  this.collaborationGraph = graph;
                  this.collaborations = mapCollaborationGraph(graph);

                  // Stale relative to the new graph. Only actually re-fetched below if the List
                  // view is (or becomes) the one showing - see loadActivities.
                  this.activities = null;
                }),
                // Downstream of every `tap` that clears the flag: upstream, a failure left the
                // overlay stuck on for good.
                catchError((err) => {
                  console.error('RelevancyCollaborations error', err);
                  this.collaborationGraph = null;
                  this.collaborations = [];
                  this.activities = [];
                  this.loading.emit(false);
                  return EMPTY;
                }),
                tap(() => {
                  this.loading.emit(false);

                  if (
                    this.collaborativeNetworkAndActivitiesListCurrentView ===
                    CollaborativeNetworkAndActivitiesListViewType.List
                  ) {
                    this.loadActivities();
                  }
                }),
              );
          }),
        )
        .subscribe(),
    );
  }

  /**
   * Only fetches the List view's works when the user is actually looking at (or switching to) it -
   * a slider move while on the Network view no longer pays for a fetch nobody sees. Matches AMS's
   * `deferActivitiesUntilList`, which this panel could not replicate before
   * `CollaborativeNetworkAndActivitiesListComponent` had a way to report its view changing.
   */
  onViewTypeChange(
    viewType: CollaborativeNetworkAndActivitiesListViewType,
  ): void {
    this.collaborativeNetworkAndActivitiesListCurrentView = viewType;

    if (viewType === CollaborativeNetworkAndActivitiesListViewType.List) {
      this.loadActivities();
    }
  }

  private loadActivities(): void {
    // Already current for this graph, or the graph has not resolved yet - the tap above will call
    // back in once it does, if the List view is still active then.
    if (this.activities || !this.collaborationGraph) {
      return;
    }

    this.loading.emit(true);
    this._subscriptions.push(
      this.collaborationGraphService
        .getNodeActivities(
          this.person.guid,
          toCollaborationActivityRefs(
            this.collaborationGraph,
            this.person.guid,
          ),
        )
        .pipe(
          catchError((err) => {
            console.error('RelevancyCollaborations activities error', err);
            this.activities = [];
            this.loading.emit(false);
            return EMPTY;
          }),
        )
        .subscribe((activities) => {
          this.activities = activities;
          this.loading.emit(false);
        }),
    );
  }

  /**
   * The inline scholars for one work in the List view. These arrive with no contributors - the
   * drill-down drops them - so each row fetches its own first page on demand.
   */
  showPeople(activity: ISTPersonActivity) {
    this._subscriptions.push(
      this.activityCollaboratorsService.fetchFirstPage(activity).subscribe({
        next: (resultSet) => {
          const row = this.activities?.find(
            (a) =>
              a.id === activity.id &&
              a.activityTypeId === activity.activityTypeId,
          );

          if (row) {
            row.contributors = this.activityCollaboratorsService.toContributors(
              resultSet?.results,
            );
            this.activities = [...this.activities];
            this.forceMapPeople = !this.forceMapPeople;
          }
        },
        error: (err) => console.error('showPeople error', err),
      }),
    );
  }

  showAllCollaborators(activity: ISTPersonActivity) {
    this.activityCollaboratorsService.openModal(activity);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showRelevancyCollaborationsDialog(target: any) {
    const nodeGuid = target.node.id;
    if (nodeGuid === this.person.guid) {
      return;
    }

    this.loading.emit(true);
    this._subscriptions.push(
      this.collaborationGraphService
        .getNodeActivities(
          nodeGuid,

          // Only the works this pair shares, which is what the dialog shows.
          toCollaborationActivityRefs(
            this.collaborationGraph,
            nodeGuid,
            this.person.guid,
          ),
        )
        .pipe(
          catchError((err) => {
            console.error('RelevancyCollaborations detail error', err);
            this.loading.emit(false);
            return EMPTY;
          }),
        )
        .subscribe((activities) => {
          // Deliberately NOT assigned to `this.activities`. That field is bound in the template
          // and backs the List view, populated in ngOnInit with the person's full related works.
          // These are only the works this pair shares, so writing them there would silently
          // shrink the List view - and `showPeople` searches the same field, so its lazy
          // collaborator load would then run against the wrong set.
          this.loading.emit(false);

          this.dialogContentService
            .setDialogContent<PeopleCollaborativeActivitiesComponent>({
              component: PeopleCollaborativeActivitiesComponent,
              dialogClasses: 'medium',
            })
            .subscribe((component) => {
              // From the graph, not from the activities: the drill-down returns works with their
              // contributors dropped, so there is no contributor list left to search.
              const collaboratorNode = (
                this.collaborationGraph?.nodes ?? []
              ).find((n) => n.id === nodeGuid);

              const collaborator =
                collaboratorNode ?
                  toCollaboratorPerson(collaboratorNode)
                : null;
              component.activities = activities;
              component.profilePerson = this.person;
              component.collaborator = collaborator;
              this._subscriptions.push(
                ...this.activityCollaboratorsService.wireLazyScholars(
                  component,
                ),
              );
            });
        }),
    );
  }

  nodeClassGenerator = (datum: ExtendedForceDirectedGraphNode) => {
    let className = '';
    if (this.institutionContext.institutionGroup) {
      const institutions =
        this.institutionContext.institutionGroup.institutions;
      if (institutions && institutions.length) {
        const inst = institutions.find(
          (institution) => institution.id === datum.primaryInstitutionId,
        );
        if (inst && inst.name) {
          className = inst.name.replace(/[^a-zA-Z\s]/g, '').toLocaleLowerCase();
        }
      }
    }
    return className;
  };
}
